import { Flex, Table, TableProps, Typography, InputRef } from "antd";
import { BusinessAdminDto } from "shared/types";
import { useNavigate } from "react-router-dom";
import PATHS from "shared/constants/paths";
import { useRef } from "react";
import { PAGE_SIZE } from "shared/constants";
import { useRememberedTablePage } from "hooks/useRememberedTablePage";
import { translate } from "shared/languages";
import RegisterAdminModal from "components/modals/RegisterAdminModal.component";
import { formatPhone } from "shared/utils/phone";
import { getColumnSearchProps } from "shared/components/SearchColumnProps";
import { StatusBadge } from "pages/schedule/component/ScheduleStatusBadge";
import { hasRole, isManager } from "shared/utils/role";
import { UserType } from "shared/types/enum";
import { ACCOUNT_STATUS_LABELS, ACCOUNT_STATUS_TONE, USER_ROLE_LABELS } from "shared/utils";

interface AdminUsersTabProps {
  businessId?: string;
  admins: BusinessAdminDto[];
  // Defaults to true for the SystemAdmin usage (embedded in a Tabs panel
  // with no title of its own, so this heading IS the section title). The
  // Manager-facing AccountsPage renders its own PageHeading above this
  // component instead, so it passes false to avoid a redundant duplicate.
  showHeading?: boolean;
  // Only the standalone Staf page passes a key; the BusinessDetails Akun
  // tab omits it, keeping that instance's pagination uncontrolled.
  pageStorageKey?: string;
}

const AdminUsersTab = ({ admins, businessId, showHeading = true, pageStorageKey }: AdminUsersTabProps) => {
  const navigate = useNavigate();
  const searchInput = useRef<InputRef>(null);
  const { page, handleTableChange } = useRememberedTablePage(pageStorageKey);

  // Manager outranks Admin, so it's always shown first — fixed data order,
  // not a togglable/resettable column sort (see the Role column below).
  const sortedAdmins = [...admins].sort((a, b) => Number(isManager(b.roles)) - Number(isManager(a.roles)));

  const columns: TableProps<BusinessAdminDto>["columns"] = [
    {
      title: translate("label.name") || "Name",
      dataIndex: "full_name",
      key: "full_name",
      sorter: (a, b) => (a.full_name > b.full_name ? 1 : -1),
      ...getColumnSearchProps({ config: "full_name", searchInput }),
    },
    {
      title: translate("label.phone_number"),
      dataIndex: "phone_number",
      key: "phone_number",
      ...getColumnSearchProps({
        config: { dataIndex: "phone_number", searchType: "phone" },
        searchInput,
      }),
      render: (text) => formatPhone(text as string),
    },
    {
      title: "Role",
      dataIndex: "roles",
      key: "role",
      // Role chips are labels (what the person is), not statuses: no dot,
      // localised like the profile page, and every role chip is navy. A
      // dual-role account (multi-role staff) also carries the Instructor
      // chip, so a Manager can see that an admin also teaches.
      render: (_, record) => {
        const manager = isManager(record.roles);
        return (
          <Flex gap={6} wrap>
            <StatusBadge
              tone="navy"
              label={USER_ROLE_LABELS[manager ? UserType.Manager : UserType.Admin]}
              dot={false}
            />
            {hasRole(record.roles, UserType.Instructor) && (
              <StatusBadge tone="navy" label={USER_ROLE_LABELS[UserType.Instructor]} dot={false} />
            )}
          </Flex>
        );
      },
      filters: [
        { value: "manager", text: "Manager" },
        { value: "admin", text: "Admin" },
      ],
      onFilter: (value, record) => (value === "manager" ? isManager(record.roles) : !isManager(record.roles)),
    },
    {
      // "Status", not "Active": the column holds both states, and "Status" is
      // the same word in both languages, which is why every other table in
      // the app titles this column exactly this way.
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      // Same Active / Inactive badges as the Klien and Karyawan lists.
      render: (is_active) =>
        is_active ? (
          <StatusBadge tone={ACCOUNT_STATUS_TONE.active} label={ACCOUNT_STATUS_LABELS.active} />
        ) : (
          <StatusBadge tone={ACCOUNT_STATUS_TONE.inactive} label={ACCOUNT_STATUS_LABELS.inactive} />
        ),
      filters: [
        {
          text: ACCOUNT_STATUS_LABELS.active,
          value: "active",
        },
        {
          text: ACCOUNT_STATUS_LABELS.inactive,
          value: "deactivated",
        },
      ],
      // Deactivated accounts are hidden by default; the filter reveals them.
      // Filter values must be strings: AntD round-trips dropdown selections as
      // string keys, so boolean values never match once the user touches it
      // (the old true/false values made this filter a no-op).
      defaultFilteredValue: ["active"],
      onFilter: (value, record) => (record.is_active ? "active" : "deactivated") === value,
    },
  ];

  return (
    <Flex vertical gap={20}>
      {showHeading && (
        <Flex vertical>
          <Typography.Title level={5} style={{ margin: 0 }}>
            {translate("title.accounts")}
          </Typography.Title>
          <Typography>{translate("subtitle.accounts")}</Typography>
        </Flex>
      )}

      <Flex justify={"flex-end"} gap={10}>
        <RegisterAdminModal businessId={businessId} />
      </Flex>

      <Table
        showSorterTooltip={false}
        sticky
        rowKey="user_id"
        size={"middle"}
        columns={columns}
        dataSource={sortedAdmins}
        onRow={(record) => {
          return {
            onClick: () => navigate(`${PATHS.STAFF}/${record.user_id}`),
            style: { cursor: "pointer" },
          };
        }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          showSizeChanger: false,
        }}
        onChange={handleTableChange}
        style={{ borderRadius: 50 }}
      />
    </Flex>
  );
};

export default AdminUsersTab;
